import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, MapPin, Zap } from "lucide-react";
import InstallAppButton from "./InstallAppButton";

const links = [
  { label: "Events", href: "#events", testid: "nav-events-link" },
  { label: "Food", href: "#food", testid: "nav-food-link" },
  { label: "Visitor Guide", href: "#guide", testid: "nav-guide-link" },
];

export default function Navbar({ onEnter, cities = [], cityId, onSwitchCity }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const current = cities.find((c) => c.id === cityId);

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-xl"
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-neon-lime" fill="currentColor" />
          <span className="font-display text-sm font-bold uppercase tracking-widest">
            {(current?.name || "Philly").toUpperCase()} <span className="text-neon-lime">//</span> After Dark
          </span>
        </a>
        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 transition-colors hover:text-neon-lime"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {cities.length > 1 && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="city-switcher-button"
              className="flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300 transition-all hover:border-neon-lime/60 hover:text-neon-lime"
            >
              <MapPin className="h-3.5 w-3.5 text-neon-lime" />
              {current ? `${current.name}, ${current.state}` : "City"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-white/10 bg-black/90 p-2 backdrop-blur-xl"
                data-testid="city-switcher-menu"
              >
                {cities.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => {
                      onSwitchCity(c.id);
                      setOpen(false);
                    }}
                    data-testid={`city-option-${c.id}`}
                    className={`flex w-full items-center justify-between rounded-lg px-4 py-3 text-left font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                      c.id === cityId ? "bg-neon-lime/10 text-neon-lime" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {c.name}, {c.state}
                    <span className="text-zinc-600">{c.pass_prefix}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </div>
          )}
          <InstallAppButton />
          <a
            href="/admin"
            data-testid="nav-admin-link"
            className="hidden font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 transition-colors hover:text-zinc-200 sm:block"
          >
            Admin
          </a>
          <button
            onClick={onEnter}
            data-testid="nav-enter-button"
            className="hidden rounded-full bg-neon-lime px-5 py-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(34,197,94,0.5)] md:block"
          >
            Enter the City
          </button>
        </div>
      </div>
    </motion.header>
  );
}
